<template>
    <div class="pic-root">
        <!-- 빈 상태 -->
        <div v-if="!product" class="pic-empty">
            <i class="fas fa-cube pic-empty-icon"></i>
            <p class="pic-empty-text">상품을 선택하세요</p>
        </div>

        <template v-else>
            <!-- 헤더 -->
            <div class="pic-header">
                <div class="pic-header-text">
                    <span class="pic-header-title">제품 상세 정보</span>
                </div>
                <CustomDropdown :items="headerMenuItems" align="right" class="pic-header-menu">
                    <template #trigger>
                        <button
                            type="button"
                            class="panel-header-button panel-header-button--dropdown-trigger"
                            title="작업"
                        >
                            <i class="fas fa-ellipsis-v"></i>
                        </button>
                    </template>
                </CustomDropdown>
            </div>

            <!-- 바디 -->
            <div class="pic-body">
                <!-- 제품 식별 -->
                <div class="pic-identity">
                    <div class="pic-product-icon">
                        <i class="fas fa-cube"></i>
                    </div>
                    <div class="pic-product-meta">
                        <span class="pic-product-code">{{ product.product_code }}</span>
                        <span class="pic-product-name">{{ product.product_name }}</span>
                    </div>
                </div>

                <!-- Unit / Status -->
                <div class="pic-attrs">
                    <div class="pic-attr">
                        <span class="pic-attr-key">상품 단위</span>
                        <span class="pic-attr-val">{{ product.product_unit || "—" }}</span>
                    </div>
                    <div class="pic-attr-divider"></div>
                    <div class="pic-attr">
                        <span class="pic-attr-key">사용 여부</span>
                        <span
                            class="pic-attr-val pic-status"
                            :class="product.use_yn === 'Y' ? 'pic-status--active' : 'pic-status--inactive'"
                        >
                            {{ product.use_yn === "Y" ? "Active" : "Inactive" }}
                        </span>
                    </div>
                    <!--
                    <div v-if="product.target_ei_unit" class="pic-attr-divider"></div>
                    <div v-if="product.target_ei_unit" class="pic-attr">
                        <span class="pic-attr-key">목표 원단위</span>
                        <span class="pic-attr-val">{{ product.target_ei_unit }}</span>
                    </div>
                    -->
                </div>

                <!-- 성능 지표 -->
                <div class="pic-section">
                    <div class="pic-section-title">성과 지표</div>

                    <div class="pic-metrics-row">
                        <div class="pic-metric">
                            <span class="pic-metric-label">현재 원단위 (Current)</span>
                            <div class="pic-metric-value-row">
                                <span class="pic-metric-current" :class="progressColorClass">
                                    {{ avgIntensityFmt }}
                                </span>
                                <span v-if="eiUnit" class="pic-metric-unit">{{ eiUnit }}</span>
                            </div>
                        </div>
                        <!--
                        <div v-if="targetEi !== null" class="pic-metric pic-metric--right">
                            <span class="pic-metric-label">목표 (Target)</span>
                            <span class="pic-metric-target">{{ targetEiFmt }}</span>
                        </div>
                        -->
                    </div>

                    <!--
                    <template v-if="targetEi !== null">
                        <div class="pic-progress-track">
                            <div
                                class="pic-progress-fill"
                                :class="progressColorClass"
                                :style="{ width: progressPct + '%' }"
                            ></div>
                        </div>
                        <div class="pic-progress-labels">
                            <span class="pic-efficiency-label" :class="efficiencyClass">
                                {{ efficiencyLabel }}
                            </span>
                            <span v-if="deviationText" class="pic-deviation" :class="progressColorClass">
                                {{ deviationText }}
                            </span>
                        </div>
                    </template>
                    -->
                </div>

                <!-- 원단위 추이 미니 차트 -->
                <div class="pic-section">
                    <div class="pic-section-head">
                        <span class="pic-section-title">원단위 추이</span>
                        <span class="pic-section-hint">Last {{ trendData.length || 0 }} days</span>
                    </div>
                    <div v-if="trendData.length" ref="trendChart" class="pic-trend-canvas"></div>
                    <div v-else class="pic-trend-empty">
                        <span>데이터 없음</span>
                    </div>
                </div>
            </div>

            <!-- 액션 버튼 -->
            <div class="pic-actions">
                <button type="button" class="pic-action-primary" @click="$emit('edit')">
                    <i class="fas fa-pencil-alt"></i> 상품 설정 수정
                </button>
            </div>

            <!-- 등록·수정 일시 (상품 설정 팝업에서 이동) -->
            <div class="pic-footer">
                <div class="pic-footer-meta">
                    <span class="pic-meta-pair">
                        <span class="pic-meta-k">등록</span>
                        <time class="pic-meta-v" :datetime="metaCreatedIso">{{ metaCreatedDisplay }}</time>
                    </span>
                    <span class="pic-meta-dot" aria-hidden="true">·</span>
                    <span class="pic-meta-pair">
                        <span class="pic-meta-k">수정</span>
                        <time class="pic-meta-v" :datetime="metaUpdatedIso">{{ metaUpdatedDisplay }}</time>
                    </span>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import * as echarts from "echarts";
import CustomDropdown from "@src/components/dropdown/CustomDropdown.vue";

export default {
    name: "ProductInfoCard",
    components: { CustomDropdown },
    props: {
        product: { type: Object, default: null },
        avgIntensity: { type: Number, default: null },
        targetEi: { type: Number, default: null },
        eiUnit: { type: String, default: "" },
        intensityRows: { type: Array, default: () => [] },
    },
    data() {
        return { chartInstance: null };
    },
    computed: {
        progressRatio() {
            if (this.avgIntensity === null || !this.targetEi) return null;
            return this.avgIntensity / this.targetEi;
        },
        progressPct() {
            const r = this.progressRatio;
            return r === null ? 0 : Math.min(r * 100, 100);
        },
        progressColorClass() {
            const r = this.progressRatio;
            if (r === null) return "";
            if (r > 1.0) return "pic-state--danger";
            if (r > 0.9) return "pic-state--warn";
            return "pic-state--ok";
        },
        efficiencyLabel() {
            const r = this.progressRatio;
            if (r === null) return "";
            return r > 1.0 ? "목표 초과" : "적합";
        },
        efficiencyClass() {
            const r = this.progressRatio;
            if (r === null) return "";
            return r > 1.0 ? "pic-eff--danger" : "pic-eff--ok";
        },
        avgIntensityFmt() {
            if (this.avgIntensity === null) return "—";
            return this.avgIntensity.toFixed(2);
        },
        targetEiFmt() {
            if (this.targetEi === null) return "—";
            return Number(this.targetEi).toFixed(2);
        },
        deviationText() {
            const r = this.progressRatio;
            if (r === null) return null;
            const pct = ((r - 1) * 100).toFixed(1);
            return "목표대비 " + (pct > 0 ? "+" : "") + pct + "%";
        },
        trendData() {
            return [...this.intensityRows]
                .filter((r) => r.intensityValue != null)
                .sort((a, b) => (a.periodKey > b.periodKey ? 1 : -1))
                .slice(-7);
        },
        metaCreatedDisplay() {
            return this.formatProductDateLabel(this.product?.created_at);
        },
        metaUpdatedDisplay() {
            return this.formatProductDateLabel(this.product?.updated_at);
        },
        metaCreatedIso() {
            return this.productDateIso(this.product?.created_at);
        },
        metaUpdatedIso() {
            return this.productDateIso(this.product?.updated_at);
        },
        headerMenuItems() {
            return [
                {
                    label: "삭제",
                    icon: "fa-trash-alt",
                    variant: "danger",
                    onClick: () => this.onHeaderDeleteClick(),
                },
            ];
        },
    },
    watch: {
        product() {
            this.destroyChart();
            this.$nextTick(() => this.initOrUpdateChart());
        },
        trendData() {
            this.$nextTick(() => this.initOrUpdateChart());
        },
    },
    mounted() {
        if (this.product && this.trendData.length) {
            this.$nextTick(() => this.initOrUpdateChart());
        }
    },
    beforeDestroy() {
        this.destroyChart();
    },
    methods: {
        parseProductDateTime(raw) {
            if (raw == null || String(raw).trim() === "") return null;
            const s = String(raw).trim();
            const normalized = s.includes("T") ? s : s.replace(" ", "T");
            const d = new Date(normalized);
            return Number.isNaN(d.getTime()) ? null : d;
        },
        formatProductDateLabel(raw) {
            const d = this.parseProductDateTime(raw);
            if (!d) return raw != null && String(raw).trim() !== "" ? String(raw) : "—";
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            const h = String(d.getHours()).padStart(2, "0");
            const min = String(d.getMinutes()).padStart(2, "0");
            return `${y}-${m}-${day} ${h}:${min}`;
        },
        productDateIso(raw) {
            const d = this.parseProductDateTime(raw);
            return d ? d.toISOString() : "";
        },
        async onHeaderDeleteClick() {
            if (!this.product || this.product.product_id == null) return;
            const name = this.product.product_name || "";
            const result = await this.alertConfirmWarning(`"${name}" 상품을 삭제하시겠습니까?`);
            if (result.value) this.$emit("delete", this.product.product_id);
        },
        initOrUpdateChart() {
            if (!this.trendData.length) {
                this.destroyChart();
                return;
            }
            const el = this.$refs.trendChart;
            if (!el) return;

            if (this.chartInstance) {
                this.chartInstance.dispose();
                this.chartInstance = null;
            }
            this.chartInstance = echarts.init(el);
            this.renderChart();
        },
        destroyChart() {
            if (this.chartInstance) {
                this.chartInstance.dispose();
                this.chartInstance = null;
            }
        },
        /** periodKey(예: 20250326) → x축에 쓸 짧은 날짜 문자열 */
        trendDateLabelFromPeriodKey(periodKey) {
            const s = String(periodKey || "").replace(/\D/g, "");
            if (s.length >= 8) {
                const m = parseInt(s.slice(4, 6), 10);
                const d = parseInt(s.slice(6, 8), 10);
                if (Number.isFinite(m) && Number.isFinite(d)) return `${m}/${d}`;
            }
            return String(periodKey || "").trim() || "—";
        },
        renderChart() {
            if (!this.chartInstance) return;
            const rows = this.trendData;
            if (!rows.length) {
                this.chartInstance.clear();
                return;
            }

            const values = rows.map((r) => Number(r.intensityValue));
            const maxVal = Math.max(...values);
            const peakIndex = values.findIndex((v) => v === maxVal);

            const barData = values.map((v) => ({
                value: v,
                itemStyle: {
                    color: v === maxVal ? "#1b4f8c" : "#c8d8ee",
                    borderRadius: [2, 2, 0, 0],
                },
            }));

            this.chartInstance.setOption(
                {
                    animation: false,
                    grid: { top: 4, right: 4, bottom: 22, left: 4, containLabel: false },
                    tooltip: {
                        trigger: "axis",
                        formatter: (params) => {
                            const p = params[0];
                            const ix = p.dataIndex;
                            const row = rows[ix];
                            const dateStr = row ? this.trendDateLabelFromPeriodKey(row.periodKey) : "";
                            const valStr = p.value != null ? Number(p.value).toFixed(3) : "—";
                            return dateStr ? `${dateStr}<br/>${valStr}` : valStr;
                        },
                        textStyle: { fontSize: 10 },
                        padding: [4, 8],
                    },
                    xAxis: {
                        type: "category",
                        data: rows.map((_, i) => i),
                        axisLine: { show: false },
                        axisTick: { show: false },
                        axisLabel: {
                            show: true,
                            interval: 0,
                            formatter: (value, index) =>
                                index === peakIndex ? this.trendDateLabelFromPeriodKey(rows[index].periodKey) : "",
                            color: "#1b4f8c",
                            fontSize: 10,
                            fontWeight: 600,
                        },
                        splitLine: { show: false },
                    },
                    yAxis: {
                        type: "value",
                        axisLine: { show: false },
                        axisTick: { show: false },
                        axisLabel: { show: false },
                        splitLine: { show: false },
                    },
                    series: [
                        {
                            type: "bar",
                            data: barData,
                            barMaxWidth: 24,
                            barCategoryGap: "20%",
                        },
                    ],
                },
                true,
            );
        },
    },
};
</script>

<style scoped>
.pic-root {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    background: #fff;
    border-radius: 10px;
}

/* 빈 상태 */
.pic-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: #94a3b8;
}

.pic-empty-icon {
    font-size: 28px;
    color: #cbd5e1;
}

.pic-empty-text {
    font-size: 12px;
    font-weight: 600;
    color: #94a3b8;
    margin: 0;
}

/* 상품 목록 헤더(.list-header)와 동일: 패딩·타이포·세로 정렬만 맞춤 (고정 height 없음) */
.pic-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 10px 14px 10px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.92);
    flex-shrink: 0;
}

.pic-header-text {
    min-width: 0;
}

.pic-header-menu {
    flex-shrink: 0;
}

/* 목록 패널 .panel-header-button 과 동일 (헤더 줄 높이 맞춤) */
.panel-header-button {
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 36px;
    min-height: 32px;
    padding: 0 11px;
    border-radius: 8px;
    border: 1px solid transparent;
    background: transparent;
    color: var(--puc-slate-500, #64748b);
    cursor: pointer;
    transition:
        color 0.18s ease,
        background 0.18s ease,
        border-color 0.18s ease;
}

.panel-header-button--dropdown-trigger:hover {
    color: var(--puc-navy, #1b263b);
    background: rgba(241, 245, 249, 0.9);
    border-color: rgba(226, 232, 240, 0.95);
}

.panel-header-button--dropdown-trigger:focus-visible {
    outline: 2px solid rgba(0, 123, 255, 0.26);
    outline-offset: 2px;
}

.pic-header-title {
    display: block;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.3;
    color: #1b263b;
    letter-spacing: -0.01em;
}

.pic-header-sub {
    display: block;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.1em;
    color: #94a3b8;
    margin-top: 1px;
}

/* 바디 */
.pic-body {
    flex: 1;
    min-height: 0;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    overflow-y: auto;
}

/* 제품 식별 */
.pic-identity {
    display: flex;
    align-items: center;
    gap: 10px;
}

.pic-product-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 14px;
    flex-shrink: 0;
}

.pic-product-meta {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.pic-product-code {
    font-size: 10px;
    font-weight: 700;
    color: #64748b;
    letter-spacing: 0.04em;
}

.pic-product-name {
    font-size: 13px;
    font-weight: 700;
    color: #1b263b;
    line-height: 1.3;
    word-break: keep-all;
}

/* 속성 행 */
.pic-attrs {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    background: #f8fafc;
    border-radius: 7px;
    border: 1px solid #e2e8f0;
    justify-content: space-around;
    text-align: center;
}

.pic-attr {
    display: flex;
    flex-direction: column;
    gap: 1px;
}

.pic-attr-key {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: #94a3b8;
}

.pic-attr-val {
    font-size: 12px;
    font-weight: 700;
    color: #1b263b;
}

.pic-attr-divider {
    width: 1px;
    height: 24px;
    background: #e2e8f0;
    flex-shrink: 0;
}

.pic-status--active {
    color: #059669;
}

.pic-status--inactive {
    color: #94a3b8;
}

/* 섹션 */
.pic-section {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.pic-section-title {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.1em;
    color: #94a3b8;
}

.pic-section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.pic-section-hint {
    font-size: 10px;
    font-weight: 600;
    color: #94a3b8;
}

/* 성능 지표 */
.pic-metrics-row {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 8px;
}

.pic-metric {
    display: flex;
    flex-direction: column;
    gap: 1px;
}

.pic-metric--right {
    align-items: flex-end;
}

.pic-metric-label {
    font-size: 9px;
    font-weight: 600;
    color: #94a3b8;
    white-space: nowrap;
}

.pic-metric-value-row {
    display: flex;
    align-items: baseline;
    gap: 3px;
}

.pic-metric-current {
    font-size: 24px;
    font-weight: 700;
    color: #1b263b;
    font-variant-numeric: tabular-nums;
    line-height: 1;
}

.pic-metric-current.pic-state--ok {
    color: #1b263b;
}
.pic-metric-current.pic-state--warn {
    color: #d97706;
}
.pic-metric-current.pic-state--danger {
    color: #f43f5e;
}

.pic-metric-unit {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
}

.pic-metric-target {
    font-size: 15px;
    font-weight: 700;
    color: #475569;
    font-variant-numeric: tabular-nums;
}

/* 진행 바 */
.pic-progress-track {
    height: 6px;
    border-radius: 999px;
    background: #e2e8f0;
}

.pic-progress-fill {
    height: 100%;
    border-radius: 999px;
    background: #2563eb;
    transition: width 0.4s ease;
}

.pic-progress-fill.pic-state--ok {
    background: #2563eb;
}
.pic-progress-fill.pic-state--warn {
    background: #f59e0b;
}
.pic-progress-fill.pic-state--danger {
    background: #f43f5e;
}

.pic-progress-labels {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.pic-efficiency-label {
    font-size: 11px;
    font-weight: 700;
}

.pic-eff--ok {
    color: #059669;
}
.pic-eff--danger {
    color: #f43f5e;
}

.pic-deviation {
    font-size: 11px;
    font-weight: 700;
    color: #2563eb;
}

.pic-deviation.pic-state--danger {
    color: #f43f5e;
}
.pic-deviation.pic-state--warn {
    color: #d97706;
}

/* 트렌드 차트 */
.pic-trend-canvas {
    width: 100%;
    height: 72px;
}

.pic-trend-empty {
    height: 72px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    color: #94a3b8;
    font-weight: 600;
}

/* 액션 버튼 */
.pic-actions {
    padding: 10px 14px 2px 14px;
    border-top: 1px solid #f1f5f9;
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex-shrink: 0;
}

.pic-action-primary {
    width: 100%;
    padding: 9px 12px;
    border-radius: 8px;
    border: none;
    background: #1b263b;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    transition: background 0.15s;
}

.pic-action-primary:hover {
    background: #24344f;
}

/* 푸터 (등록·수정 일시) */
.pic-footer {
    padding: 6px 14px 10px;
    flex-shrink: 0;
}

.pic-footer-meta {
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    line-height: 1.35;
    color: #64748b;
}

.pic-meta-pair {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
}

.pic-meta-k {
    font-weight: 600;
    color: #94a3b8;
}

.pic-meta-v {
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    color: #475569;
}

.pic-meta-dot {
    color: #cbd5e1;
    user-select: none;
}
</style>
